/**
 * Report-level queries: run once when the page loads, then KPI / gauge /
 * progress widgets pick fields from those result sets.
 */

import { parseSqlSelectKeys } from './buildJoinSql';

export const REPORT_DATA_SOURCE = 'report';

function nextId(prefix, existing = []) {
  const used = new Set(existing.map((x) => x.id));
  let i = 1;
  while (used.has(`${prefix}_${i}`)) i += 1;
  return `${prefix}_${i}`;
}

export function emptyReportQuery(existing = []) {
  return {
    id: nextId('query', existing),
    name: `کوئری ${(existing || []).length + 1}`,
    sqlQuery: '',
    joinConfig: null,
    fields: [],
    limit: 500,
  };
}

export function normalizeReportQuery(raw = {}, index = 0) {
  return {
    id: String(raw.id || `query_${index + 1}`),
    name: raw.name || `کوئری ${index + 1}`,
    sqlQuery: String(raw.sqlQuery || raw.sql || raw.query?.sql || '').trim(),
    joinConfig: raw.joinConfig || null,
    fields: Array.isArray(raw.fields) ? raw.fields : [],
    limit: Math.min(Math.max(1, Number(raw.limit) || 500), 10000),
  };
}

export function normalizeReportQueries(queries) {
  if (!Array.isArray(queries)) return [];
  return queries.map((q, i) => normalizeReportQuery(q, i));
}

export function isReportDataSource(widget) {
  const src = widget?.dataSource;
  if (src === REPORT_DATA_SOURCE || src === 'report_queries') return true;
  if (src === 'sql' || src === 'dashboard' || src === 'formula' || src === 'sales' || src === 'targets') {
    return false;
  }
  if (widget?.reportQueryId) return true;
  return false;
}

/** KPI / gauge / progress default to shared report queries (not their own SQL). */
export function widgetUsesReportQueries(widget) {
  if (!widget) return false;
  if (isReportDataSource(widget)) return true;
  const type = widget.type;
  if (type === 'stat_card' || type === 'gauge' || type === 'progress_bar') {
    if (widget.dataSource === 'sql' || widget.dataSource === 'dashboard' || widget.dataSource === 'formula') {
      return false;
    }
    return true;
  }
  return false;
}

export function firstRowOf(result) {
  if (!result || !Array.isArray(result.rows) || result.rows.length === 0) return {};
  return result.rows[0] || {};
}

export function mergeQueryFirstRows(resultsById = {}, queryId) {
  if (queryId && resultsById[queryId]) {
    return { ...firstRowOf(resultsById[queryId]) };
  }
  const merged = {};
  Object.keys(resultsById).forEach((id) => {
    Object.assign(merged, firstRowOf(resultsById[id]));
  });
  return merged;
}

export function fieldsFromResult(result) {
  const cols = result?.columns || [];
  return cols.map((c) => {
    if (typeof c === 'string') return { key: c, fa: c };
    return { key: c.key || c.name, fa: c.label || c.fa || c.key || c.name };
  }).filter((f) => f.key);
}

export function catalogFieldsFromQueries(queries = [], resultsById = {}) {
  const seen = new Set();
  const out = [];
  (queries || []).forEach((q) => {
    const fromResult = fieldsFromResult(resultsById[q.id]);
    const listed = Array.isArray(q.fields) ? q.fields.map((f) => (
      typeof f === 'string' ? { key: f, fa: f } : { key: f.key, fa: f.fa || f.label || f.key }
    )) : [];
    const fromSql = parseSqlSelectKeys(q.sqlQuery).map((key) => ({ key, fa: key }));
    [...listed, ...fromResult, ...fromSql].forEach((f) => {
      if (!f?.key || seen.has(f.key)) return;
      seen.add(f.key);
      out.push({ ...f, queryId: q.id, queryName: q.name });
    });
  });
  return out;
}

/** The one report query a widget should read. Multiple queries require an explicit pick. */
export function selectedReportQuery(widget, queries = []) {
  const list = normalizeReportQueries(queries);
  if (widget?.reportQueryId) {
    return list.find((q) => q.id === widget.reportQueryId) || null;
  }
  return list.length === 1 ? list[0] : null;
}

/**
 * Column/formula pickers: only fields from the selected query (or the widget SQL).
 * Never dumps the dashboard catalog (sale_pct, targeted_sales, …).
 */
export function fieldsForWidget(widget, queries = [], sqlKeys = []) {
  if (widgetUsesReportQueries(widget)) {
    const selected = selectedReportQuery(widget, queries);
    return selected ? catalogFieldsFromQueries([selected]) : [];
  }
  const seen = new Set();
  const out = [];
  (sqlKeys || []).forEach((raw) => {
    const key = typeof raw === 'string' ? raw : raw?.key;
    if (!key || seen.has(key)) return;
    seen.add(key);
    out.push({
      key,
      fa: (typeof raw === 'object' && (raw.fa || raw.label)) || key,
    });
  });
  return out;
}

export function resultForWidget(widget, resultsById = {}, queries = []) {
  const selected = selectedReportQuery(widget, queries);
  const id = selected?.id || widget?.reportQueryId;
  if (id && resultsById[id]) {
    return resultsById[id];
  }
  if (queries.length === 1 && resultsById[queries[0].id]) {
    return resultsById[queries[0].id];
  }
  return { columns: [], rows: [] };
}
